import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';
import type {
  CreateSupportTicketInput,
  PaginatedSupportTickets,
  Severity,
  SupportTicket,
  SupportTicketComment,
  TicketStatus,
} from './types';

const TICKETS_KEY = ['support-tickets'] as const;

export interface ListFilters {
  status?: TicketStatus | 'all';
  severity?: Severity;
  assigneeUserId?: string;
  reporterUserId?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(filters: ListFilters): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.assigneeUserId) params.set('assigneeUserId', filters.assigneeUserId);
  if (filters.reporterUserId) params.set('reporterUserId', filters.reporterUserId);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  return params.toString();
}

export function useSupportTickets(filters: ListFilters = {}) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: [...TICKETS_KEY, filters],
    queryFn: () =>
      apiFetch<PaginatedSupportTickets>(
        `/api/v1/support-tickets?${buildQuery(filters)}`,
        { getAccessToken },
      ),
  });
}

/**
 * W47-F7-S1 — customer-facing /support page hook. Calls the server-side
 * `GET /api/v1/support-tickets/mine` endpoint, which forces `reporterUserId`
 * to the requesting user's `oid` so this can never accidentally widen out
 * to another user's tickets.
 */
export function useMySupportTickets(
  filters: Omit<ListFilters, 'assigneeUserId' | 'reporterUserId'> = {},
) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: [...TICKETS_KEY, 'mine', filters],
    queryFn: () =>
      apiFetch<PaginatedSupportTickets>(
        `/api/v1/support-tickets/mine?${buildQuery(filters)}`,
        { getAccessToken },
      ),
  });
}

export function useSupportTicket(id: string | undefined) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: [...TICKETS_KEY, 'detail', id],
    enabled: !!id,
    queryFn: () =>
      apiFetch<SupportTicket>(
        `/api/v1/support-tickets/${encodeURIComponent(id ?? '')}`,
        { getAccessToken },
      ),
  });
}

export function useSupportTicketComments(id: string | undefined) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: [...TICKETS_KEY, 'comments', id],
    enabled: !!id,
    queryFn: () =>
      apiFetch<SupportTicketComment[]>(
        `/api/v1/support-tickets/${encodeURIComponent(id ?? '')}/comments`,
        { getAccessToken },
      ),
  });
}

export function useCreateSupportTicket() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupportTicketInput) =>
      apiFetch<SupportTicket>('/api/v1/support-tickets', {
        method: 'POST',
        getAccessToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        // Avoid recursion: a 5xx on the FeedbackModal POST shouldn't
        // surface another "Report this?" toast that opens the same modal.
        skipServerErrorReport: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

export function useUpdateSupportTicket(id: string | undefined) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      status?: TicketStatus;
      assigneeUserId?: string | null;
      resolutionNotes?: string;
    }) =>
      apiFetch<SupportTicket>(
        `/api/v1/support-tickets/${encodeURIComponent(id ?? '')}`,
        {
          method: 'PATCH',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

/**
 * W47-F7-S1 — customer-initiated close of a ticket. Backend enforces
 * ownership (reporter or admin); FE just PATCHes the dedicated close
 * endpoint. Invalidates both the detail + my-tickets queries on success.
 */
export function useCloseSupportTicket(id: string | undefined) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SupportTicket>(
        `/api/v1/support-tickets/${encodeURIComponent(id ?? '')}/close`,
        {
          method: 'PATCH',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });
}

export function useAddSupportTicketComment(id: string | undefined) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; internal: boolean }) =>
      apiFetch<SupportTicketComment>(
        `/api/v1/support-tickets/${encodeURIComponent(id ?? '')}/comments`,
        {
          method: 'POST',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      if (!id) return;
      void queryClient.invalidateQueries({
        queryKey: [...TICKETS_KEY, 'comments', id],
      });
    },
  });
}
